<template>
  <div
    class="w-full h-full bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6 sm:p-8"
  >
    <!-- Header -->
    <div class="mb-6">
      <div
        class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5"
      >
        <!-- Title -->
        <div class="flex items-center gap-2">
          <BarChart3 class="w-5 h-5 text-indigo-600" />
          <h2 class="text-sm font-semibold text-slate-700 tracking-wide">
            Student Attendance Analytics
          </h2>
        </div>

        <!-- Right Side Filters -->
        <div
          class="flex flex-wrap items-center justify-start lg:justify-end gap-3"
        >
          <!-- Date Range Dropdown -->
          <select
            v-model="dateRangeOption"
            @change="handleDateRangeChange"
            class="px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
          >
            <option
              v-for="option in dateRangeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>

          <!-- Year (Only for Full Year & Custom Range) -->
          <div
            v-if="dateRangeOption === 'full' || dateRangeOption === 'custom'"
            class="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-slate-300 shadow-sm"
          >
            <label class="text-xs font-medium text-slate-600">Year</label>
            <input
              v-model.number="selectedYearLocal"
              type="number"
              class="w-20 text-sm border-none focus:outline-none focus:ring-0 bg-transparent"
            />
          </div>

          <!-- Custom Range (Appears Inline) -->
          <div
            v-if="dateRangeOption === 'custom'"
            class="flex items-center gap-2"
          >
            <input
              v-model="startDate"
              type="date"
              class="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none transition"
            />
            <span class="text-slate-400 text-sm">to</span>
            <input
              v-model="endDate"
              type="date"
              class="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none transition"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- Summary Cards -->
    <div class="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      <!-- Present -->
      <div class="bg-white rounded-xl p-5 border shadow-sm">
        <div class="flex items-center justify-between mb-2">
          <p class="text-xs font-semibold text-gray-500 uppercase">Present</p>
          <CheckCircle2 class="w-5 h-5 text-emerald-500" />
        </div>
        <p class="text-2xl font-bold text-gray-900">
          {{ attendanceData?.summary?.present ?? 0 }}
        </p>
      </div>

      <!-- Absent -->
      <div class="bg-white rounded-xl p-5 border shadow-sm">
        <div class="flex items-center justify-between mb-2">
          <p class="text-xs font-semibold text-gray-500 uppercase">Absent</p>
          <XCircle class="w-5 h-5 text-red-500" />
        </div>
        <p class="text-2xl font-bold text-gray-900">
          {{ attendanceData?.summary?.absent ?? 0 }}
        </p>
      </div>

      <!-- Late -->
      <div class="bg-white rounded-xl p-5 border shadow-sm">
        <div class="flex items-center justify-between mb-2">
          <p class="text-xs font-semibold text-gray-500 uppercase">Late</p>
          <Clock class="w-5 h-5 text-amber-500" />
        </div>
        <p class="text-2xl font-bold text-gray-900">
          {{ attendanceData?.summary?.late ?? 0 }}
        </p>
      </div>

      <!-- Percentage -->
      <div class="bg-white rounded-xl p-5 border shadow-sm">
        <div class="flex items-center justify-between mb-2">
          <p class="text-xs font-semibold text-gray-500 uppercase">
            Attendance %
          </p>
          <BarChart3 class="w-5 h-5 text-indigo-500" />
        </div>
        <p class="text-2xl font-bold text-gray-900">
          {{ attendanceData?.summary?.attendancePercentage ?? "0%" }}
        </p>
      </div>
    </div>

    <!-- Chart -->
    <div
      v-if="attendanceData?.monthlyStats"
      class="bg-white rounded-2xl p-6 border"
    >
      <h3 class="text-lg font-bold text-slate-900 mb-6">
        Monthly Attendance Overview
      </h3>
      <div class="relative h-80 sm:h-96 w-full">
        <canvas ref="chartRef"></canvas>
      </div>
    </div>

    <!-- Empty -->
    <div
      v-else
      class="flex flex-col items-center justify-center py-12"
    >
      <BarChart3 class="w-12 h-12 text-slate-300 mb-4" />
      <p class="text-slate-500 text-lg font-medium">
        No attendance data available.
      </p>
    </div>
  </div>
</template>

<script setup>
  import { ref, watch, nextTick, onMounted, onUnmounted, computed } from "vue";
  import { Chart, registerables } from "chart.js/auto";
  import { CheckCircle2, XCircle, Clock, BarChart3 } from "lucide-vue-next";

  Chart.register(...registerables);

  /* -----------------------------
   PROPS
----------------------------- */
  const props = defineProps({
    selectedYear: Number,
    attendanceData: Object,
  });

  const emit = defineEmits(["update:selectedYear", "filter-change"]);

  /* -----------------------------
   YEAR
----------------------------- */
  const selectedYearLocal = ref(props.selectedYear || new Date().getFullYear());

  watch(selectedYearLocal, (val) => {
    emit("update:selectedYear", val);
    emitFilters();
  });

  watch(
    () => props.selectedYear,
    (val) => {
      selectedYearLocal.value = val;
    },
  );

  /* -----------------------------
   DATE RANGE
----------------------------- */
  const dateRangeOption = ref("full");
  const startDate = ref("");
  const endDate = ref("");

  const currentYear = new Date().getFullYear();

  const isCurrentYear = computed(() => selectedYearLocal.value === currentYear);

  const dateRangeOptions = computed(() => {
    if (isCurrentYear.value) {
      return [
        { label: "Full Year", value: "full" },
        { label: "Today", value: "today" },
        { label: "Yesterday", value: "yesterday" },
        { label: "Last 7 Days", value: "last7days" },
        { label: "Last 30 Days", value: "last30days" },
        { label: "Custom Range", value: "custom" },
      ];
    }

    return [
      { label: "Full Year", value: "full" },
      { label: "Custom Range", value: "custom" },
    ];
  });

  function handleDateRangeChange() {
    const today = new Date();
    const format = (d) => d.toISOString().split("T")[0];

    switch (dateRangeOption.value) {
      case "today":
        startDate.value = format(today);
        endDate.value = format(today);
        break;

      case "yesterday":
        // eslint-disable-next-line no-case-declarations
        const y = new Date();
        y.setDate(today.getDate() - 1);
        startDate.value = format(y);
        endDate.value = format(y);
        break;

      case "last7days":
        // eslint-disable-next-line no-case-declarations
        const past7 = new Date();
        past7.setDate(today.getDate() - 6);
        startDate.value = format(past7);
        endDate.value = format(today);
        break;

      case "last30days":
        // eslint-disable-next-line no-case-declarations
        const past30 = new Date();
        past30.setDate(today.getDate() - 29);
        startDate.value = format(past30);
        endDate.value = format(today);
        break;

      default:
        startDate.value = "";
        endDate.value = "";
    }

    emitFilters();
  }

  function emitFilters() {
    const formatToMonthDay = (date) => {
      if (!date) return null;
      const [, month, day] = date.split("-");
      return `${month}-${day}`;
    };

    emit("filter-change", {
      year: selectedYearLocal.value,
      startDate: formatToMonthDay(startDate.value),
      endDate: formatToMonthDay(endDate.value),
    });
  }

  watch([startDate, endDate], () => {
    if (
      dateRangeOption.value === "custom" &&
      startDate.value &&
      endDate.value
    ) {
      emitFilters();
    }
  });

  /* -----------------------------
   CHART
----------------------------- */

  const chartRef = ref(null);
  let chartInstance = null;

  watch(
    () => props.attendanceData,
    async (data) => {
      if (!data) return;

      await nextTick();

      if (chartInstance) {
        chartInstance.destroy();
        chartInstance = null;
      }

      renderChart(data);
    },
  );

  /* -----------------------------
   CHART RENDER
----------------------------- */

  function renderChart(data) {
    const { monthlyStats = {}, dailyStats = {} } = data;

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    let labels = [];
    let present = [];
    let absent = [];
    let late = [];

    if (dateRangeOption.value === "full") {
      labels = months;

      present = months.map((m) => monthlyStats[m]?.present ?? 0);
      absent = months.map((m) => monthlyStats[m]?.absent ?? 0);
      late = months.map((m) => monthlyStats[m]?.late ?? 0);
    } else {
      const dates = [];
      const start = new Date(startDate.value);
      const end = new Date(endDate.value);

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const iso = new Date(d).toISOString().split("T")[0];
        dates.push(iso);
      }

      labels = dates.map((d) =>
        new Date(d).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
      );

      present = dates.map((d) => dailyStats[d]?.present ?? 0);
      absent = dates.map((d) => dailyStats[d]?.absent ?? 0);
      late = dates.map((d) => dailyStats[d]?.late ?? 0);
    }

    const ctx = chartRef.value.getContext("2d");

    // gradients
    const greenGradient = ctx.createLinearGradient(0, 0, 0, 400);
    greenGradient.addColorStop(0, "#34d399");
    greenGradient.addColorStop(1, "#059669");

    const redGradient = ctx.createLinearGradient(0, 0, 0, 400);
    redGradient.addColorStop(0, "#f87171");
    redGradient.addColorStop(1, "#dc2626");

    const orangeGradient = ctx.createLinearGradient(0, 0, 0, 400);
    orangeGradient.addColorStop(0, "#fbbf24");
    orangeGradient.addColorStop(1, "#d97706");

    chartInstance = new Chart(ctx, {
      type: "bar",

      data: {
        labels,

        datasets: [
          {
            label: "Present",
            data: present,
            backgroundColor: greenGradient,
            borderRadius: 12,
            barThickness: 34,
            hoverBackgroundColor: "#10b981",
            stack: "attendance",
          },
          {
            label: "Absent",
            data: absent,
            backgroundColor: redGradient,
            borderRadius: 12,
            barThickness: 34,
            hoverBackgroundColor: "#ef4444",
            stack: "attendance",
          },
          {
            label: "Late",
            data: late,
            backgroundColor: orangeGradient,
            borderRadius: 12,
            barThickness: 34,
            hoverBackgroundColor: "#f59e0b",
            stack: "attendance",
          },
        ],
      },

      options: {
        responsive: true,
        maintainAspectRatio: false,

        animation: {
          duration: 900,
          easing: "easeOutQuart",
        },

        plugins: {
          legend: {
            position: "top",
            align: "center",
            labels: {
              boxWidth: 28,
              boxHeight: 14,
              padding: 20,
              font: {
                size: 13,
                weight: "600",
              },
            },
          },

          tooltip: {
            backgroundColor: "#1e293b",
            titleFont: { size: 13 },
            bodyFont: { size: 13 },
            padding: 10,
            cornerRadius: 6,
          },
        },

        layout: {
          padding: {
            top: 15,
          },
        },

        scales: {
          x: {
            stacked: true,
            grid: {
              display: true,
              color: "rgba(0,0,0,0.04)",
            },
            ticks: {
              font: {
                size: 12,
              },
            },
          },

          y: {
            stacked: true,
            beginAtZero: true,
            grid: {
              color: "rgba(0,0,0,0.04)",
            },
            ticks: {
              stepSize: 1,
              font: {
                size: 12,
              },
            },
          },
        },
      },
    });
  }

  /* -----------------------------
   MOUNT
----------------------------- */

  onMounted(() => {
    if (props.attendanceData) {
      renderChart(props.attendanceData);
    }
  });

  onUnmounted(() => {
    if (chartInstance) chartInstance.destroy();
  });
</script>
